import React from 'react';

import { CircadianEvent, DARK_LIGHT_RECIPE_ID, LightRecipeDoc, RecipeSection } from '@ledium/types';
import cloneDeep from 'clone-deep';

import lediumApi from '../utils/lediumApi';

interface ChartPoint {
    time: number;
    value: number;
    color?: { uv: number; blue: number; green: number; red: number; fr: number; ir: number };
}

interface SpectrumEntry {
    pfd: number;
    uv: number;
    blue: number;
    green: number;
    red: number;
    fr: number;
    ir: number;
}

const useSectionData = (section: RecipeSection) => {
    const [sectionData, setSectionData] = React.useState<RecipeSection>({
        ...section,
        circadianEvents: section.circadianEvents ? cloneDeep(section.circadianEvents) : []
    });
    const [spectrumData, setSpectrumData] = React.useState<Record<string, SpectrumEntry>>({});
    const [chartData, setChartData] = React.useState<ChartPoint[]>([]);

    const loadSection = (sectionToLoad: RecipeSection) => {
        setSectionData({
            ...sectionToLoad,
            circadianEvents: sectionToLoad.circadianEvents ? cloneDeep(sectionToLoad.circadianEvents) : []
        });
    };

    React.useEffect(() => {
        if (section.id) {
            setChartData([]);
            setSectionData({
                ...section,
                circadianEvents: section.circadianEvents ? cloneDeep(section.circadianEvents) : []
            });
        }
    }, [section]);

    React.useEffect(() => {
        if (sectionData.circadianEvents.length) {
            const lightRecipeCache: Record<string, LightRecipeDoc> = {};

            (async () => {
                let newSpectrumData: Record<string, SpectrumEntry> = {};
                for (const i in sectionData.circadianEvents) {
                    const event = sectionData.circadianEvents[i];
                    if (event.lightRecipeId && event.lightRecipeId !== DARK_LIGHT_RECIPE_ID && spectrumData[event.lightRecipeId] === undefined) {
                        if (!lightRecipeCache[event.lightRecipeId]) {
                            const fetched = await lediumApi.get<LightRecipeDoc>(`lightRecipes/${event.lightRecipeId}`);
                            if (fetched) {
                                lightRecipeCache[event.lightRecipeId] = fetched;
                            }
                        }
                        if (lightRecipeCache[event.lightRecipeId]?.spectrogram) {
                            const recipe = lightRecipeCache[event.lightRecipeId];
                            const lightRecipeSumLight = Number.isInteger(event.sumLightsIntensity)
                                ? (event.sumLightsIntensity as number)
                                : recipe.spectrogramRanges.pfd.sum;
                            newSpectrumData = {
                                ...newSpectrumData,
                                [event.lightRecipeId]: {
                                    pfd: lightRecipeSumLight,
                                    uv: recipe.spectrogramRanges.pfd.uv,
                                    blue: recipe.spectrogramRanges.pfd.blue,
                                    green: recipe.spectrogramRanges.pfd.green,
                                    red: recipe.spectrogramRanges.pfd.red,
                                    fr: recipe.spectrogramRanges.pfd.fr,
                                    ir: recipe.spectrogramRanges.pfd.ir
                                }
                            };
                        }
                    }
                }
                if (Object.keys(newSpectrumData).length) {
                    setSpectrumData({ ...spectrumData, ...newSpectrumData });
                }
            })();
            const chart: ChartPoint[] = [];
            [...sectionData.circadianEvents]
                .sort((a, b) => (+a.hour + a.minute / 60 > +b.hour + b.minute / 60 ? 1 : -1))
                .forEach((event: CircadianEvent) => {
                    const entry = event.lightRecipeId ? spectrumData[event.lightRecipeId] : undefined;
                    const value = entry ? entry.pfd : 0;
                    const point: ChartPoint = { time: event.hour + event.minute / 60, value };
                    if (entry) {
                        point.color = { uv: entry.uv, blue: entry.blue, green: entry.green, red: entry.red, fr: entry.fr, ir: entry.ir };
                    }
                    chart.push(point);
                });
            setChartData(chart);
        }
    }, [sectionData.circadianEvents, spectrumData]);

    return { sectionData, setSectionData, chartData, loadSection };
};

export default useSectionData;
